import validator from 'validator';
import classNames from 'classnames';
import { RootState } from 'redux/store';
import useToggle from 'hooks/useToggle';
import { useEffect, useState, useRef } from 'react';
import { getOwners } from 'redux/slices/Owners';
import { Title, FormInput, Spinner, Modal } from 'components';
import { getStoreGroups } from 'redux/slices/Accounts';
import PasswordChecklist from 'react-password-checklist';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Card, Col, Row, Form, Button, Alert } from 'react-bootstrap';
import { getStoreById, cleanStoreDetails, getStores, updateStoreField, updateStoreDetails, createStore } from 'redux/slices/Stores';

export function NewStore() {
	const form = useRef(null);
	const { storeId } = useParams();
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const [isValidated, setValidated] = useToggle();
	const [errorMessage, setErrorMessage] = useState<string>('');
	const store = useAppSelector((state: RootState) => state.stores);
	const accounts = useAppSelector((state: RootState) => state.account);
	const owners = useAppSelector((state: RootState) => state.owners);
	const [isNewStore, setIsNewStore] = useState<boolean>(false);
	const [showPassword, togglePassword] = useToggle();
	const currentStoreGroupId = useAppSelector((state: RootState) => state.global.storeGroupId);
	const [showPrompt, setShowPrompt] = useState<boolean>(false);

	useEffect(() => {
		dispatch(getStoreGroups());
		dispatch(getOwners());
		if (storeId) {
			dispatch(getStoreById(parseInt(storeId)));
			setIsNewStore(false);
		} else {
			dispatch(cleanStoreDetails());
			setIsNewStore(true);
		}
	}, [storeId]);

	useEffect(() => {
		dispatch(updateStoreField({ key: "password", value: "" }));
		dispatch(updateStoreField({ key: "confirmPassword", value: "" }));
		if (store.setNewPassword) {
			dispatch(updateStoreField({ key: "readyToSave", value: false }));
		} else {
			dispatch(updateStoreField({ key: "readyToSave", value: true }));
		}

	}, [store.setNewPassword]);

	useEffect(() => {
		if (isNewStore || store.setNewPassword) {
			let isValid = false;
			if (store.password === store.confirmPassword) {
				isValid = true;
			}

			isValid = isValid && validator.isStrongPassword(store.password, {
				minLength: 8, minLowercase: 1,
				minUppercase: 1, minNumbers: 0, minSymbols: 1
			});
			
			dispatch(updateStoreField({ key: "readyToSave", value: isValid }));
		}
	}, [store.password, store.confirmPassword]);

	function handleSubmit() {
		dispatch(updateStoreField({ key: "loading", value: true }));
		// @ts-ignore
		if (form.current.checkValidity() === true) {
			if (!isNewStore) {
				dispatch(updateStoreDetails(store)).then(() => {
					handleResponse();
				});
			} else {
				dispatch(createStore(store)).then(() => {
					handleResponse();
				});
			}
		} else {
			dispatch(updateStoreField({ key: "loading", value: false }));
		}
		setValidated();
	}

	function handleResponse() {
		dispatch(getStores({ storeGroupIds: [currentStoreGroupId] }));
		setTimeout(() => {
			navigate('/support/stores');
		}, 1000);
	}


	function handleChange(option: any, optionValue: any) {
		dispatch({
			type: 'storesSlice/updateStoreField',
			payload: { key: option, value: optionValue }
		});
	}

	return (
		<>
			<Title pageTitle={!isNewStore ? "Update Store" : "New Store"} parent="Admin" />
			<Row>
				<Col xs={12}>
					<Card>
						<Card.Body>
							<Row className="mb-1">
								<Form validated={isValidated} ref={form}>
									<Row>
										<h4 className="mt-2">Store Details</h4>
										<p className="text-muted">Fill the form below create a new store.</p>
										<Col md={6}>
											<FormInput
												label="Store ID"
												type="number"
												name="store-id"
												placeholder="Enter store id"
												containerClass={'mb-3'}
												required
												disabled={!isNewStore}
												value={store.id}
												onChange={(e) => handleChange("id", e.target.value)}
											/>
										</Col>
									</Row>
									<Row>
										{errorMessage && <p><Alert variant="warning">{errorMessage}</Alert></p>}
										<Col md={6}>
											<FormInput
												label="Name"
												type="text"
												name="store-name"
												placeholder="Enter store name"
												containerClass={'mb-3'}
												required
												value={store.name}
												onChange={(e) => handleChange("name", e.target.value)}
											/>
										</Col>
										<Col md={6}>
											<FormInput
												label="Username"
												type="text"
												name="store-username"
												placeholder="Enter store username"
												containerClass={'mb-3'}
												required
												value={store.username}
												onChange={(e) => handleChange("username", e.target.value)}
											/>
										</Col>
									</Row>
									<Row >
										<Col md={6} className="mb-3">
											<Form.Group>
												<Form.Label className="form-check-label" htmlFor="exampleCheckbox">Owner</Form.Label>
												<Form.Select className="custom-select"
													required
													defaultValue={store.ownerId.toString()}
													onChange={(e) => handleChange("ownerId", parseInt(e.target.value))}
												>
													<option hidden value='' selected={store.ownerId === 0}>Choose a store group</option>
													{owners.table.map((storeGroup: any, index: any) => {
														return <option key={index} value={storeGroup.id.toString()} selected={store.ownerId.toString() === storeGroup.id.toString()}>{storeGroup.name}</option>
													})
													}
												</Form.Select>
											</Form.Group>
										</Col>
									</Row>

									{!isNewStore &&
										<Row>
											<Col md={6} className="mb-3">
												<Form.Group>
													<Form.Label className="form-check-label" htmlFor="exampleCheckbox">Password Reset</Form.Label>
													<div>
														<Form.Check
															type="switch"
															id="stock-active"
															label=""
															checked={store.setNewPassword}
															onChange={() => {
																handleChange("setNewPassword", !store.setNewPassword);
															}}
														/>
													</div>
												</Form.Group>
											</Col>
											<Col md={6} className="mb-3">
												<Form.Group>
													<Form.Label className="form-check-label" htmlFor="exampleCheckbox">Status</Form.Label>
													<div>
														<Form.Check
															type="switch"
															id="stock-active"
															label={store.status}
															checked={store.status === "Active" && true}
															onChange={(e) => {
																if (store.status === "Active") {
																	handleChange('status', "Suspended")
																} else {
																	handleChange('status', "Active")
																}
															}}
														/>
													</div>
												</Form.Group>
											</Col>
										</Row>
									}

									{isNewStore || store.setNewPassword ? (
										<>
											<Row>
												<Col md={6} className="mb-3">
													<Form.Group>
														<Form.Label htmlFor="password">Password</Form.Label>
														<div className="mb-0 input-group">
															<Form.Control
																type={showPassword ? 'text' : 'password'}
																name="password"
																placeholder="Enter store password"
																required
																value={store.password}
																onChange={(e) => {
																	handleChange("password", e.target.value)
																}}
															/>
															<div className={classNames('input-group-text', 'input-group-password', { 'show-password': showPassword, })} data-password={showPassword ? 'true' : 'false'}>
																<span className="password-eye" onClick={togglePassword}></span>
															</div>
														</div>
													</Form.Group>
												</Col>
												<Col md={6}>
													<Form.Group>
														<Form.Label htmlFor="password">Confirm Password</Form.Label>
														<div className="mb-0 input-group">
															<Form.Control
																type={showPassword ? 'text' : 'password'}
																name="confirm-password"
																placeholder="Enter store confirm password"
																required
																value={store.confirmPassword}
																onChange={(e) => {
																	handleChange("confirmPassword", e.target.value);
																}}
															/>
															<div className={classNames('input-group-text', 'input-group-password', { 'show-password': showPassword, })} data-password={showPassword ? 'true' : 'false'}>
																<span className="password-eye" onClick={togglePassword}></span>
															</div>
														</div>
													</Form.Group>
													{store.confirmPassword.length > 0 && <>
														{store.password !== store.confirmPassword && <Form.Text className="text-danger">Password does not match.</Form.Text>}
													</>
													}
												</Col>
											</Row>
											<Row>
												<Col md={6}>
													<PasswordChecklist
														rules={["minLength", "lowercase", "capital", "number", "specialChar", "match"]}
														minLength={8}
														value={store.password}
														className="password-checklist"
														valueAgain={store.confirmPassword}
													/>
												</Col>
											</Row>
										</>
									) : (<></>)}

									<Row className='mt-3'>
										<Col sm={6}>
											<Link to="/support/stores" className="btn text-muted d-none d-sm-inline-block btn-link fw-semibold">
												<i className="mdi mdi-arrow-left"></i> Back to Stores {' '}
											</Link>
										</Col>
										<Col sm={6} className="d-flex flex-row-reverse">
											<Button variant="primary" className="btn cta-button" disabled={!store.readyToSave} onClick={() => setShowPrompt(true)}>
												{store.loading ? <><Spinner className="spinner-border-sm me-1" tag="span" color="white" /></> : <></>}
												{store.loading ? <>Saving...</> : <>Save</>}
											</Button>
										</Col>
									</Row>
								</Form>
							</Row>
						</Card.Body>
					</Card>
				</Col>
			</Row>

			<Modal
				title="Confirm - Save Store"
				show={showPrompt}
				buttonType="primary"
				disableButtons={false}
				buttonText={"Accept"}
				handleClose={() => setShowPrompt(false)}
				handleSubmit={() => {
					handleSubmit();
					setShowPrompt(false);
				}}
				description={
					<>
						<p>Confirm Details</p>
						<p>Are you sure you want to continue?</p>
					</>
				}
			/>
		</>
	);
}